import "bootstrap-icons/font/bootstrap-icons.css";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./product.css";

import { Button, Dropdown, Menu, Segmented, Table, Popover, Tooltip, message, Modal } from "antd";
import { DeleteOutlined, EditOutlined, BarcodeOutlined, MoreOutlined, StockOutlined, HistoryOutlined, PlusOutlined } from "@ant-design/icons";
import { debounce } from "../../utils/debounce";

import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { loadProduct, getAllProductAction } from "../../redux/actions/product/getAllProductAction";
import { loadSingleProduct } from "../../redux/actions/product/detailProductAction";
import { deleteProduct } from "../../redux/actions/product/deleteProductAction";
import { globalProductSearch } from "../../redux/actions/product/globalSearchAction";
import GenerateBarcodePopUp from "./generateBarcodePopUp";
import GenerateBarcode from "./barcodeGenerator";
import StockViewModal from "./components/StockViewModal";
import ProductHistoryModal from "./components/ProductHistoryModal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import UploadProductExcel from "./UploadProductExcel";
import AddProd from "./addProd";

/** My Stock UI: show products that have `stock` ledger rows (any net qty, including negative). */
const productHasLedgerStockRows = (p) => {
  const rows = p?.stocks_by_location;
  return Array.isArray(rows) && rows.length > 0;
};

// Barcode Content Component - shows barcode directly without extra button
const BarcodeContent = ({ isbn, onClose }) => {
  const [count, setCount] = useState(1);

  const handleCount = (e) => {
    e.preventDefault();
    setCount(e.target.value);
  };

  const handlePrint = (e) => {
    e.preventDefault();
    const input = document.getElementById("barcode");
    if (input) {
      html2canvas(input).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const width = pdf.internal.pageSize.getWidth();
        const height = pdf.internal.pageSize.getHeight();
        pdf.addImage(imgData, "JPEG", 0, 0, width, height);
        pdf.save(`${moment().format("L")}_${isbn}_Barcode.pdf`);
      });
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <div className='d-flex justify-content-center'>
      <form onSubmit={onSubmit}>
        <div className='mb-3 text-center'>
          <label htmlFor='count' className='form-label me-3'>
            Number of barcodes:
          </label>
          <input
            type='number'
            className='form-control'
            id='count'
            value={count}
            onChange={handleCount}
          />
        </div>
      </form>
      <div>
        <Button className='mt-2' type='primary' onClick={handlePrint}>
          Print Barcode
        </Button>
      </div>
      <div id="barcode">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} style={{ marginBottom: '20px' }}>
            <GenerateBarcode isbn={isbn} />
          </div>
        ))}
      </div>
    </div>
  );
};

function CustomTable({ list, tableLoading, total, status, viewMode, setEditData, setEditMode, setAddProdVisible, searchValue, handleSearchChange, columns, columnsToShow, setColumnsToShow, setBarcodeModalVisible, setSelectedProduct, setStockModalVisible, setSelectedProductForStock, setHistoryModalVisible, setSelectedProductForHistory, barcodeModalVisible, selectedProduct, sortField, sortOrder, onTableChange, getSortParams, currentPage, pageSize }) {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [tableScrollY, setTableScrollY] = useState(400);
  const tableWrapRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tableWrapRef.current) return;
    const el = tableWrapRef.current;
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      const headerPagination = 88;
      setTableScrollY(Math.max(200, h - headerPagination));
    });
    ro.observe(el);
    const h = el.clientHeight;
    setTableScrollY(Math.max(200, h - 88));
    return () => ro.disconnect();
  }, [list]);

  useEffect(() => {
    const rows = list ?? [];
    setAllData(rows);
    setFilteredData(rows);
  }, [list]);

  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.id }));

  return (
    <div className="product-list-table-inner">
      <div className="product-list-table-inner-fill">
        {/* Desktop Table - fixed header, scrollable body, pagination below */}
        <div ref={tableWrapRef} className="d-none d-lg-block product-list-table-wrap">
          <Table
            scroll={{ x: true, y: tableScrollY }}
            loading={tableLoading === true}
            pagination={{
              current: currentPage || 1,
              pageSize: pageSize || 10,
              pageSizeOptions: [10, 20, 50, 100, 200],
              showSizeChanger: true,
              total: filteredData?.length ?? 0,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={(pagination, filters, sorter, extra) => {
              if (onTableChange) onTableChange(pagination, filters, sorter, extra);
              else dispatch(loadProduct({ status, viewMode, onlyLedgerStock: viewMode === "stock", ...(getSortParams ? getSortParams() : {}) }));
            }}
            columns={columnsToShow}
            dataSource={filteredData ? addKeys(filteredData) : []}
            onRow={(record) => {
              console.log("Table row data:", record);
              return {
                onClick: () => console.log("Row clicked:", record),
              };
            }}
          />
        </div>

        {/* Mobile Card Layout - scrollable so small screens can see all products */}
        <div className="d-lg-none product-list-mobile-cards">
          {filteredData && filteredData.length > 0 ? (
            <div className="row">
              {filteredData.map((record) => (
                <div key={record.id} className="col-12 mb-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="row align-items-center">
                        {/* Image */}
                        <div className="col-3">
                          <img 
                            src={record.imageUrl} 
                            alt="product" 
                            className="img-fluid rounded"
                            style={{ maxHeight: '100px', width: '100%', objectFit: 'cover' }}
                          />
                        </div>
                        
                        {/* Content */}
                        <div className="col-9">
                          <h6 className="card-title mb-1">
                            <Link to={`/product/${record.id}`} style={{ textDecoration: 'none', color: '#1890ff' }}>
                              {record.name}
                            </Link>
                          </h6>
                          <p className="card-text mb-1">
                            <small className="text-muted">
                              <strong>ISBN:</strong> {record.isbn}
                            </small>
                          </p>
                          <p className="card-text mb-1">
                            <small className="text-muted">
                              <strong>Author:</strong> {record.author || 'N/A'}
                            </small>
                          </p>
                          <p className="card-text mb-1">
                            <small className="text-muted">
                              <strong>Publisher:</strong> {record.book_publisher?.name || 'N/A'}
                            </small>
                          </p>
                          {record.stocks_by_location && record.stocks_by_location.length > 1 ? (
                            <>
                              {record.stocks_by_location.map((r, idx) => (
                                <p key={r.location_id ?? idx} className="card-text mb-1">
                                  <small className="text-muted">
                                    <strong>{r.location_name}</strong> — <strong>Qty:</strong> {r.quantity}
                                  </small>
                                </p>
                              ))}
                              <p className="card-text mb-1">
                                <small className="text-muted">
                                  <strong>Price:</strong>{" "}
                                  {(() => {
                                    const sym = record.product_currency?.symbol ?? "";
                                    const uniq = [
                                      ...new Set(
                                        record.stocks_by_location.map((r) =>
                                          Number(r.sale_price ?? record.sale_price)
                                        )
                                      ),
                                    ].filter((n) => !Number.isNaN(n));
                                    if (uniq.length === 0) return "-";
                                    return uniq
                                      .map((p) => `${sym}${p.toLocaleString("en-IN")}`)
                                      .join(", ");
                                  })()}
                                </small>
                              </p>
                            </>
                          ) : (
                            <p className="card-text mb-1">
                              <small className="text-muted">
                                <strong>Qty:</strong> {record.quantity} |{" "}
                                <strong>Price:</strong> {record.product_currency?.symbol}
                                {record.sale_price != null
                                  ? Number(record.sale_price).toLocaleString("en-IN")
                                  : "-"}
                              </small>
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions - Kebab menu */}
                      <div className="mt-2">
                        <Dropdown
                          overlay={
                            <Menu
                              items={[
                                { key: "edit", icon: <EditOutlined />, label: "Edit" },
                                { key: "delete", icon: <DeleteOutlined />, label: "Delete", danger: true },
                                { key: "barcode", icon: <BarcodeOutlined />, label: "Barcode" },
                                { key: "stock", icon: <StockOutlined />, label: "Stock" },
                                { key: "history", icon: <HistoryOutlined />, label: "Product History" },
                              ]}
                              onClick={(e) => {
                                e.domEvent?.preventDefault?.();
                                e.domEvent?.stopPropagation?.();
                                const key = e.key;
                                if (key === "edit") {
                                  dispatch(loadSingleProduct(record.id)).then((res) => {
                                    const product = res?.data ?? res;
                                    if (product && typeof product === "object" && product.id) {
                                      setEditData(product);
                                      setEditMode(true);
                                      setTimeout(() => setAddProdVisible(true), 0);
                                    } else {
                                      message.error("Failed to load product details");
                                    }
                                  }).catch(() => {
                                    message.error("Failed to load product details");
                                  });
                                } else if (key === "delete") {
                                  Modal.confirm({
                                    title: "Are you sure you want to delete this product?",
                                    content: `This will permanently delete "${record.name}" and cannot be undone.`,
                                    okText: "Yes, Delete",
                                    okType: "danger",
                                    cancelText: "Cancel",
                                    onOk() {
                                      try {
                                        dispatch(deleteProduct(record.id));
                                        message.warning(`Product: ${record.name} is removed`);
          dispatch(loadProduct({ status: "true", viewMode, onlyLedgerStock: viewMode === "stock", ...(getSortParams ? getSortParams() : {}) }));
                                      } catch (error) {
                                        console.log(error.message);
                                        message.error("Failed to delete product");
                                      }
                                    },
                                  });
                                } else if (key === "barcode") {
                                  setSelectedProduct(record);
                                  setBarcodeModalVisible(true);
                                } else if (key === "stock") {
                                  setSelectedProductForStock(record);
                                  setStockModalVisible(true);
                                } else if (key === "history") {
                                  setSelectedProductForHistory(record);
                                  setHistoryModalVisible(true);
                                }
                              }}
                            />
                          }
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <span>
                            <Button type="default" size="small" icon={<MoreOutlined />}>
                              Actions
                            </Button>
                          </span>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">No products found</p>
            </div>
          )}
          
          {/* Mobile Pagination */}
          {filteredData && filteredData.length > 0 && (
            <div className="d-flex justify-content-center mt-3">
              <Button 
                type="primary" 
                onClick={() => {
                  // Implement mobile pagination logic here
                  console.log("Load more products");
                }}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Barcode Modal */}
      <Modal
        title="Generate Barcode"
        open={barcodeModalVisible}
        onCancel={() => setBarcodeModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedProduct && (
          <BarcodeContent 
            isbn={selectedProduct.isbn ? selectedProduct.isbn : 0} 
            onClose={() => setBarcodeModalVisible(false)}
          />
        )}
      </Modal>
    </div>
  );
}

const GetAllProd = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const list = useSelector((state) => state.products.list);
  const singleProductFromRedux = useSelector((state) => state.products?.product);
  const globalSearchResults = useSelector((state) => state.products?.globalSearchResults);
  const [status, setStatus] = useState("true");
  const [viewMode, setViewMode] = useState("stock");
  // Sort state for table (Ant: ascend | descend) — must be above hooks that use getSortParams
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const getSortParams = () => {
    const by = sortField ? (sortField === "book_publisher" ? "publisher" : sortField) : undefined;
    const order = sortOrder === "ascend" ? "asc" : sortOrder === "descend" ? "desc" : undefined;
    return { sort_by: by, sort_order: order };
  };

  useEffect(() => {
    dispatch(
      loadProduct({
        status,
        viewMode,
        onlyLedgerStock: viewMode === "stock",
        ...getSortParams(),
      })
    );
  }, [viewMode, dispatch, location.pathname, status]);

  const [addProdVisible, setAddProdVisible] = useState(false);
  const [importCsvVisible, setImportCsvVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [barcodeModalVisible, setBarcodeModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [selectedProductForStock, setSelectedProductForStock] = useState(null);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);

  const sortProductList = (items, field, order) => {
    if (!field || !order) return items;
    const dir = order === "ascend" ? 1 : -1;
    const getVal = (p) => {
      if (field === "book_publisher") return (p.book_publisher?.name || "").toString().toLowerCase();
      if (field === "name" || field === "author" || field === "isbn") {
        return (p[field] || "").toString().toLowerCase();
      }
      if (field === "quantity" || field === "sale_price" || field === "purchase_price") {
        return Number(p[field]) || 0;
      }
      const v = p[field];
      return typeof v === "string" ? v.toLowerCase() : v ?? "";
    };
    return [...(items || [])].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  };

  // Debounced search function for server-side search
  const debouncedSearch = useCallback(
    debounce(async (searchTerm) => {
        if (!searchTerm || searchTerm.trim() === '') {
        dispatch(loadProduct({ status, viewMode, onlyLedgerStock: viewMode === "stock", ...getSortParams() }));
        setCurrentPage(1);
        return;
      }

      try {
        // Use Redux action for global search
        const result = await dispatch(
          globalProductSearch(searchTerm.trim(), 1, 1000, {
            onlyLedgerStock: viewMode === "stock",
          })
        );
        if (result && result.status === "success") {
          // Update the list with search results
          dispatch(getAllProductAction(result.data || []));
          setCurrentPage(1);
        }
      } catch (error) {
        console.error('Search error:', error);
      }
    }, 300), // 300ms delay
    [dispatch, viewMode, sortField, sortOrder, status]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setCurrentPage(1);
    debouncedSearch(value);
  };

  // Column visibility functionality
  const [columnItems, setColumnItems] = useState([]);
  const [columnsToShow, setColumnsToShow] = useState([]);

  const listForDisplay = useMemo(() => {
    if (list == null) return null;
    if (viewMode !== "stock") return list;
    return list.filter(productHasLedgerStockRows);
  }, [list, viewMode]);

  const total = listForDisplay?.length ?? 0;

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      const filteredColumnsToShow = columnsToShow.filter(
        (item) => item.key !== col.key
      );
      setColumnsToShow(filteredColumnsToShow);
    } else {
      const foundIndex = columns.findIndex((item) => item.key === col.key);
      const foundCol = columns.find((item) => item.key === col.key);
      let updatedColumnsToShow = [...columnsToShow];
      updatedColumnsToShow.splice(foundIndex, 0, foundCol);
      setColumnsToShow(updatedColumnsToShow);
    }
  };

  const onChange = (value) => {
    setStatus(value);
    dispatch(loadProduct({ status: value, viewMode, onlyLedgerStock: viewMode === "stock", ...getSortParams() }));
  };

  const handleViewModeChange = (value) => {
    setViewMode(value);
    dispatch(loadProduct({ status, viewMode: value, onlyLedgerStock: value === "stock", ...getSortParams() }));
  };

  // Define columns for table
  const columns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 56,
      render: (imageUrl) => (
        <img
          style={{
            maxWidth: 40,
            maxHeight: 36,
            width: 40,
            height: 36,
            objectFit: "cover",
            display: "block",
          }}
          alt="product"
          src={imageUrl}
        />
      ),
    },
   
    {
      title: "ISBN",
      dataIndex: "isbn",
      key: "isbn",
    },
    {
      title: "Title",
      dataIndex: "name",
      key: "name",
      sorter: true,
      width: 220,
      className: "product-list-title-cell",
      render: (name, record) => (
        <Link
          to={`/product/${record.id}`}
          style={{ textDecoration: "none", color: "#1890ff", whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {name}
        </Link>
      ),
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
      sorter: true,
    },
    {
      title: "Publisher",
      dataIndex: "book_publisher",
      key: "book_publisher",
      sorter: true,
      render: (book_publisher) => book_publisher?.name,
    },
    {
      title: (
        <Tooltip title="Quantity (per location when multiple stocks)">
          <span>Qty</span>
        </Tooltip>
      ),
      dataIndex: "quantity",
      key: "quantity",
      sorter: (a, b) => (a.quantity ?? 0) - (b.quantity ?? 0),
      render: (_, record) => {
        const rows = record.stocks_by_location;
        if (rows && rows.length > 1) {
          return (
            <div style={{ fontSize: 12, lineHeight: 1.45 }}>
              {rows.map((r, i) => (
                <div key={r.location_id ?? i}>
                  <span style={{ fontWeight: 600 }}>{r.location_name}</span>: {r.quantity}
                </div>
              ))}
            </div>
          );
        }
        return record.quantity ?? 0;
      },
    },
    {
      title: (
        <Tooltip title="Currency">
          <span>Curr</span>
        </Tooltip>
      ),
      dataIndex: "product_currency",
      key: "product_currency",
      render: (product_currency) => product_currency?.symbol ?? "-",
    },
    {
      title: (
        <Tooltip title="Sale price">
          <span>Price</span>
        </Tooltip>
      ),
      dataIndex: "sale_price",
      key: "sale_price",
      sorter: true,
      responsive: ["md"],
      render: (sale_price, record) => {
        const rows = record.stocks_by_location;
        const sym = record.product_currency?.symbol ?? "";
        if (rows && rows.length > 1) {
          const uniqPrices = [
            ...new Set(rows.map((r) => Number(r.sale_price ?? sale_price))),
          ].filter((n) => !Number.isNaN(n));
          if (uniqPrices.length === 0) return "-";
          return (
            <div style={{ fontSize: 12, lineHeight: 1.45 }}>
              {uniqPrices.map((p, i) => (
                <div key={i}>
                  {sym}
                  {p.toLocaleString("en-IN")}
                </div>
              ))}
            </div>
          );
        }
        if (sale_price == null) return "-";
        return `${sym}${Number(sale_price).toLocaleString("en-IN")}`;
      },
    },
    {
      title: "Category",
      dataIndex: "categories",
      key: "categories",
      render: (categories, record) => {
        // Support both new format (categories array) and old format (single product_category)
        if (categories && Array.isArray(categories) && categories.length > 0) {
          return categories.map(cat => cat.name || cat).join(", ");
        } else if (record.product_category?.name) {
          return record.product_category.name;
        }
        return "-";
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => {
        const handleActionMenuClick = (e) => {
          e.domEvent?.preventDefault?.();
          e.domEvent?.stopPropagation?.();
          const { key } = e;
          if (key === "edit") {
            dispatch(loadSingleProduct(record.id)).then((res) => {
              const product = res?.data ?? res;
              if (product && typeof product === "object" && product.id) {
                setEditData(product);
                setEditMode(true);
                // Open modal on next tick so AddProd receives editMode=true and editData before mounting
                setTimeout(() => setAddProdVisible(true), 0);
              } else {
                message.error("Failed to load product details");
              }
            }).catch(() => {
              message.error("Failed to load product details");
            });
          } else if (key === "delete") {
            Modal.confirm({
              title: "Are you sure you want to delete this product?",
              content: `This will permanently delete "${record.name}" and cannot be undone.`,
              okText: "Yes, Delete",
              okType: "danger",
              cancelText: "Cancel",
              onOk() {
                try {
                  dispatch(deleteProduct(record.id));
                  message.warning(`Product: ${record.name} is removed`);
                  dispatch(loadProduct({ status: "true", viewMode, onlyLedgerStock: viewMode === "stock", ...(getSortParams ? getSortParams() : {}) }));
                } catch (error) {
                  console.log(error.message);
                  message.error("Failed to delete product");
                }
              },
            });
          } else if (key === "barcode") {
            setSelectedProduct(record);
            setBarcodeModalVisible(true);
          } else if (key === "stock") {
            setSelectedProductForStock(record);
            setStockModalVisible(true);
          } else if (key === "history") {
            setSelectedProductForHistory(record);
            setHistoryModalVisible(true);
          }
        };
        const actionMenuItems = [
          { key: "edit", icon: <EditOutlined />, label: "Edit" },
          { key: "delete", icon: <DeleteOutlined />, label: "Delete", danger: true },
          { key: "barcode", icon: <BarcodeOutlined />, label: "Barcode" },
          { key: "stock", icon: <StockOutlined />, label: "Stock" },
          { key: "history", icon: <HistoryOutlined />, label: "Product History" },
        ];
        return (
          <Dropdown
            overlay={<Menu items={actionMenuItems} onClick={handleActionMenuClick} />}
            trigger={["click"]}
            placement="bottomRight"
          >
            <span onClick={(ev) => ev.stopPropagation()}>
              <Button htmlType="button" type="text" size="small" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
            </span>
          </Dropdown>
        );
      },
    }
  ];

  const menuItems = columns.map((item) => {
    return {
      key: item.key,
      label: item.title,
    };
  });

  // When "All Books" is selected, hide Quantity column; inject sortOrder into sortable columns
  const effectiveColumns = (viewMode === "all" ? columnsToShow.filter((c) => c.key !== "quantity") : columnsToShow).map((col) => ({
    ...col,
    sortOrder: col.sorter && sortField === col.key ? sortOrder : undefined,
  }));

  const handleTableChange = (pagination, filters, sorter, extra) => {
    if (pagination?.current != null) setCurrentPage(pagination.current);
    if (pagination?.pageSize != null) {
      if (pagination.pageSize !== pageSize) setCurrentPage(1);
      setPageSize(pagination.pageSize);
    }

    const nextField = sorter?.order ? sorter.field ?? null : null;
    const nextOrder = sorter?.order || null;
    const isSortAction =
      extra?.action === "sort" ||
      (!extra?.action && (nextField !== sortField || nextOrder !== sortOrder));

    // Pagination/filter is client-side. Reloading here wiped active search results.
    if (!isSortAction) {
      return;
    }

    setSortField(nextField);
    setSortOrder(nextOrder);

    const sortBy = nextField
      ? nextField === "book_publisher"
        ? "publisher"
        : nextField
      : undefined;
    const sortOrderApi =
      nextOrder === "ascend" ? "asc" : nextOrder === "descend" ? "desc" : undefined;

    // Keep search results; sort them client-side instead of loading all products
    if (searchValue?.trim()) {
      dispatch(getAllProductAction(sortProductList(list, nextField, nextOrder)));
      return;
    }

    dispatch(
      loadProduct({
        status,
        viewMode,
        onlyLedgerStock: viewMode === "stock",
        sort_by: sortBy,
        sort_order: sortOrderApi,
      })
    );
  };

  // Initialize column visibility
  useEffect(() => {
    setColumnItems(menuItems);
    setColumnsToShow(columns);
  }, []);

  const CSVlist = (listForDisplay ?? []).map((i) => {
    // Support both new format (categories array) and old format (single product_category)
    let categoryName = "-";
    if (i.categories && Array.isArray(i.categories) && i.categories.length > 0) {
      categoryName = i.categories.map(cat => cat.name || cat).join(", ");
    } else if (i.product_category?.name) {
      categoryName = i.product_category.name;
    }
    return {
      ...i,
      product_category: categoryName,
    };
  });

  return (
    <div className="card column-design product-list-page">
      <div className="card-body list-page-card-body">
        {/* On mobile this wrapper sticks to top so header + filters stay visible */}
        <div className="list-page-sticky-top">
        {/* Header: Products List left, search + Add Book + Import From Excel in one line right */}
        <div className="mb-1">
          <div className="d-flex flex-nowrap justify-content-between align-items-center gap-2  products-list-header-row">
            <h4 className="mb-0 flex-shrink-0">Products List</h4>
            <div className="d-flex flex-nowrap align-items-center gap-2 flex-grow-1 justify-content-end min-w-0">
              <input
                type="text"
                placeholder="Search Book by ISBN, Title, Author, or Publisher..."
                value={searchValue}
                onChange={handleSearchChange}
                className="form-control products-list-search"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditMode(false);
                  setEditData(null);
                  setAddProdVisible(true);
                }}
                className="btn-add-book-green flex-shrink-0"
              >
                Add Book
              </Button>
              <Button
                type="default"
                icon={<i className="bi bi-upload"></i>}
                onClick={() => setImportCsvVisible(true)}
                className="flex-shrink-0"
              >
                Import From Excel
              </Button>
            </div>
          </div>

          {/* Filters and other actions */}
          <div className="d-flex flex-wrap align-items-center">
            {list != null && (
              <CSVLink
                data={CSVlist}
                className="btn btn-dark btn-sm"
                filename="products"
              >
                <i className="bi bi-download me-1"></i>
                <span className="d-none d-sm-inline">Download CSV</span>
                <span className="d-sm-none">CSV</span>
              </CSVLink>
            )}
            {list != null && (
              <>
                <Segmented
                  className="text-center rounded"
                  size="middle"
                  options={[
                    {
                      label: (
                        <span>
                          <i className="bi bi-box-seam"></i> My Stock
                        </span>
                      ),
                      value: "stock",
                    },
                    {
                      label: (
                        <span>
                          <i className="bi bi-book"></i> All Books
                        </span>
                      ),
                      value: "all",
                    },
                  ]}
                  value={viewMode}
                  onChange={handleViewModeChange}
                  style={{ marginRight: "8px" }}
                />
                <Segmented
                  className="text-center rounded danger"
                  size="middle"
                  options={[
                    {
                      label: (
                        <span>
                          <i className="bi bi-person-lines-fill"></i> Active
                        </span>
                      ),
                      value: "true",
                    },
                    {
                      label: (
                        <span>
                          <i className="bi bi-person-dash-fill"></i> Inactive
                        </span>
                      ),
                      value: "false",
                    },
                  ]}
                  value={status}
                  onChange={onChange}
                />
              </>
            )}
            {list != null && (
              <Dropdown
                overlay={
                  <Menu onClick={colVisibilityClickHandler} items={columnItems} />
                }
                placement="bottomLeft"
              >
                <Button className="column-visibility">Column Visibility</Button>
              </Dropdown>
            )}
          </div>
        </div>
        </div>

        <div className="product-list-table-container">
          <CustomTable 
          list={listForDisplay ?? []} 
          tableLoading={list == null}
          total={total} 
          status={status}
          viewMode={viewMode}
          setEditData={setEditData}
          setEditMode={setEditMode}
          setAddProdVisible={setAddProdVisible}
          searchValue={searchValue}
          handleSearchChange={handleSearchChange}
          columns={columns}
          columnsToShow={effectiveColumns}
          setColumnsToShow={setColumnsToShow}
          setBarcodeModalVisible={setBarcodeModalVisible}
          setSelectedProduct={setSelectedProduct}
          setStockModalVisible={setStockModalVisible}
          setSelectedProductForStock={setSelectedProductForStock}
          setHistoryModalVisible={setHistoryModalVisible}
          setSelectedProductForHistory={setSelectedProductForHistory}
          barcodeModalVisible={barcodeModalVisible}
          selectedProduct={selectedProduct}
          sortField={sortField}
          sortOrder={sortOrder}
          onTableChange={handleTableChange}
          getSortParams={getSortParams}
          currentPage={currentPage}
          pageSize={pageSize}
        />
        </div>
      </div>
      
      
      {/* Import From Excel Modal */}
      <Modal
        title="Import From Excel"
        open={importCsvVisible}
        onCancel={() => {
          setImportCsvVisible(false);
          dispatch(loadProduct({ status: "true", viewMode, onlyLedgerStock: viewMode === "stock", ...getSortParams() }));
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <UploadProductExcel
          onSuccess={() => {
            setImportCsvVisible(false);
            dispatch(loadProduct({ status: "true", viewMode, onlyLedgerStock: viewMode === "stock", ...getSortParams() }));
          }}
        />
      </Modal>

      {/* Add/Edit Product Modal */}
      <Modal
        title={editMode ? "Edit Book" : "Add New Book"}
        open={addProdVisible}
        onCancel={() => {
          setAddProdVisible(false);
          setEditMode(false);
          setEditData(null);
          // Refresh the product list when modal is closed
                                      dispatch(loadProduct({ status: "true", viewMode, onlyLedgerStock: viewMode === "stock", ...getSortParams() }));
        }}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        bodyStyle={{ paddingTop: 12 }}
        destroyOnClose={true}
      >
        <AddProd 
          editMode={editMode}
          editData={editData ?? (editMode ? singleProductFromRedux : null)}
          onClose={() => {
            setAddProdVisible(false);
            setEditMode(false);
            setEditData(null);
                                      dispatch(loadProduct({ status: "true", viewMode, onlyLedgerStock: viewMode === "stock", ...getSortParams() }));
          }}
        />
      </Modal>

      {/* Barcode Modal */}
      <Modal
        title="Generate Barcode"
        open={barcodeModalVisible}
        onCancel={() => setBarcodeModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedProduct && (
          <BarcodeContent
            isbn={selectedProduct.isbn ? selectedProduct.isbn : 0}
            onClose={() => setBarcodeModalVisible(false)}
          />
        )}
      </Modal>

      {/* Stock View Modal */}
      <StockViewModal
        productId={selectedProductForStock?.id}
        productName={selectedProductForStock?.name}
        visible={stockModalVisible}
        onClose={() => {
          setStockModalVisible(false);
          setSelectedProductForStock(null);
        }}
      />

      {/* Product History Modal */}
      <ProductHistoryModal
        productId={selectedProductForHistory?.id}
        productName={selectedProductForHistory?.name}
        visible={historyModalVisible}
        onClose={() => {
          setHistoryModalVisible(false);
          setSelectedProductForHistory(null);
        }}
      />
    </div>
  );
};

export default GetAllProd;
